import _ from "lodash";
import getPath from "lodash/get";
import type { Dispatch, Unsubscribe } from "redux";

import { store } from "../../single-spa/store/store";

const shallowEqual = (valueA: Record<string, unknown>, valueB: Record<string, unknown>) => {
    const keysA = Object.keys(valueA);
    const keysB = Object.keys(valueB);
    return keysA.length === keysB.length && keysA.every((key) => valueB?.[key] && valueA[key] === valueB[key]);
};

interface ISwRedux {
    getState(): any;
    dispatch: Dispatch<any>;
    subscribe(listener: () => void): Unsubscribe;
    getStatePath(path: string): any;
    notifyOnChange: (statePath: string, callback: (path: string, newValue: any) => void) => Unsubscribe;
    connect(
        mapStateToTarget?: (state: any) => Record<string, any>,
        mapDispatchToTarget?: Record<string, Function> | ((dispatch: Dispatch<any>) => Record<string, Function>),
    ): (target: any) => () => void;
}

export class SwRedux implements ISwRedux {
    private static _instance = new SwRedux();

    static getInstance() {
        if (!SwRedux._instance) {
            SwRedux._instance = new SwRedux();
        }
        return SwRedux._instance;
    }

    public getState(): any {
        return store?.getState();
    }

    dispatch<T = any>(action: any): T {
        return store?.dispatch(action);
    }

    getStatePath<T = any>(path: string): T {
        return getPath(store?.getState(), path);
    }

    subscribe(listener: () => void): Unsubscribe {
        return store?.subscribe(listener);
    }

    notifyOnChange(statePath: string, callback: (path: string, newValue: any) => void): Unsubscribe {
        let oldValue = this.getStatePath(statePath);
        return this.subscribe(() => {
            const newValue = this.getStatePath(statePath);
            if (oldValue !== newValue) {
                oldValue = newValue;
                callback(statePath, newValue);
            }
        });
    }

    connect(
        mapStateToTarget?: (state: any) => Record<string, any>,
        mapDispatchToTarget?: Record<string, Function> | ((dispatch: Dispatch<any>) => Record<string, Function>),
    ): (target: any) => () => void {
        let dispatchDerived: any;
        if (!mapDispatchToTarget) {
            dispatchDerived = { dispatch: this.dispatch };
        } else if (typeof mapDispatchToTarget === "function") {
            dispatchDerived = mapDispatchToTarget(this.dispatch);
        } else {
            dispatchDerived = Object.entries(mapDispatchToTarget).reduce(
                (acc, [actionName, actionFunc]) => ({
                    ...acc,
                    [actionName]: (...args: any[]) => this.dispatch(actionFunc(...args)),
                }),
                {},
            );
        }

        const updateTarget = (argTarget: any, argStateDerived: any, argDispatchDerived: any) => {
            if (typeof argTarget === "function") {
                argTarget(argStateDerived, argDispatchDerived);
            } else {
                Object.assign(argTarget, argStateDerived, argDispatchDerived);
            }
        };

        return (target: any) => {
            let stateDerived = {};
            let unsubscribe;
            if (mapStateToTarget) {
                unsubscribe = this.subscribe(() => {
                    const nextStateDerived = mapStateToTarget(this.getState());
                    if (!shallowEqual(nextStateDerived, stateDerived)) {
                        stateDerived = nextStateDerived;
                        updateTarget(target, stateDerived, dispatchDerived);
                    }
                });
            } else {
                // if no mapStateToProps, then do not subscribe at all, but still initially update target once
                updateTarget(target, stateDerived, dispatchDerived);
                unsubscribe = _.noop;
            }

            return unsubscribe;
        };
    }
}
